/* eslint-disable max-len */
/**
 * @file 财务路由
 * @author gaoxunchen
 */

import React from 'react';
import loadable from '@loadable/component';
import { Loading } from '@tencent/ptp-fe-common';

import { ACCOUNT_TYPE, AD_PLATFORM_TYPE_NAME } from '@/constants/Constants';
import { isBoss, ROLES_NAME_TO_ID } from '@/pages/agency/permission-config';
import { hasFeatureFlag } from '@/services';

export default [
  {
    label: '财务',
    key: 'finance',
    desc: '管理服务商各资金账户，支持对账查询、发票开具，支持为子客转账',
    disabled: (loginStatus: Record<string, any>) => {
      const disabledByWhiteList = hasFeatureFlag('is_od_agency') ? !isBoss(loginStatus?.role_list) : false;
      return loginStatus.account_type === ACCOUNT_TYPE.direct || loginStatus.role_list[ROLES_NAME_TO_ID.AGENCYOPERATORROLE_AGENCY_OBSERVER_OPERATOR] || disabledByWhiteList || loginStatus.onlyTechAgency;
    },
    data: [
      {
        label: '财务管理',
        key: 'finance-manage',
        iconClassName: 'icon-nav-finance',
        data: [
          {
            label: '财务信息',
            href: '/agency/finance-info/tencent',
            path: '/agency/finance-info/:type',
            key: 'gdtinfo',
            purview: 22,
            basePage: true,
            data: [
              {
                id: 'gdtinfo',
                href: '/agency/finance-info/tencent',
                label: 'ADQ营销',
                platform: AD_PLATFORM_TYPE_NAME.ADQ,
                purview: 22,
                basePage: true,
                pagePurview: {
                  total: 23,
                  table: 24,
                  trans_in: 25,
                  trans_out: 26,
                  cash_recharge: 3019,
                  get_remit_info: 3020,
                  creat_remit_info: 3021,
                  add_remit_info: 3022,
                  update_remit_info: 3023,
                  transfer_child_agency: 3049,
                },
                component: loadable(() => import(/* webpackChunkName: 'finance' */ /* webpackMode: 'lazy' */ '@/pages/agency/finance/gdtinfo/Layout'), { fallback: <Loading /> }),
                behavior: {
                  transOut: '转出资金',
                  recharge: '充值',
                  transIn: '转入资金',
                  batchSelfCare: '批量自理',
                  batchAutoTransfer: '批量自动转账',
                  batchTransIn: '批量转入',
                  funddetails: '查看资金详情',
                  editColumn: '编辑自定义列',
                  changeColumn: '切换自定义列',
                  saveColumn: '保存自定义列',
                },
              },
              {
                id: 'wxinfo',
                href: '/agency/finance-info/wechat',
                label: 'WX旧营销',
                tip: '原MP营销',
                platform: AD_PLATFORM_TYPE_NAME.MP,
                purview: 22,
                enableKey: 'is_wechat_finance_enable',
                basePage: true,
                pagePurview: {
                  total: 3024,
                  table: 3025,
                  trans_in: 3026,
                  trans_out: 3027,
                  recharge: 3023,
                  get_remit_info: 3020,
                  creat_remit_info: 3021,
                  add_remit_info: 3022,
                  update_remit_info: 3023,
                },
                component: loadable(() => import(/* webpackChunkName: 'finance' */ /* webpackMode: 'lazy' */ '@/pages/agency/finance/wxinfo/Layout'), { fallback: <Loading /> }),
                behavior: {
                  transOut: '转出资金',
                  recharge: '充值',
                  transIn: '转入资金',
                  batchTransIn: '批量转入',
                  funddetails: '查看资金详情',
                  editColumn: '编辑自定义列',
                  changeColumn: '切换自定义列',
                  saveColumn: '保存自定义列',
                },
              },
              {
                id: 'cpsinfo',
                href: '/agency/finance-info/cps',
                label: 'CPS商品营销',
                platform: AD_PLATFORM_TYPE_NAME.CPS,
                purview: 22,
                enableKey: 'is_cps_agency_enable',
                basePage: true,
                pagePurview: {
                  total: 23,
                  table: 24,
                  trans_in: 25,
                  trans_out: 26,
                  cash_recharge: 3019,
                  get_remit_info: 3020,
                  creat_remit_info: 3021,
                  add_remit_info: 3022,
                  update_remit_info: 3023,
                  transfer_child_agency: 3049,
                },
                component: loadable(() => import(/* webpackChunkName: 'finance' */ /* webpackMode: 'lazy' */ '@/pages/agency/finance/cpsinfo/Layout'), { fallback: <Loading /> }),
                behavior: {
                  transOut: '转出资金',
                  recharge: '充值',
                  transIn: '转入资金',
                  batchTransIn: '批量转入',
                  funddetails: '查看资金详情',
                  editColumn: '编辑自定义列',
                  changeColumn: '切换自定义列',
                  saveColumn: '保存自定义列',
                },
              },
            ],
          },
          // 微前端接入
          {
            label: '共享钱包',
            key: 'shared_wallet',
            href: '/agency/shared-wallet/list',
            path: '/agency/shared-wallet/:type',
            purview: 22,
            platform: AD_PLATFORM_TYPE_NAME.ADQ,
            enableKey: 'is_enable_wallet_v2',
            data: [
              {
                key: 'shared_wallet__list',
                href: '/agency/shared-wallet/list',
                purview: 22,
                isMicroApp: true,
                hideTabNav: true,
                // accountSelect: 'single', // 用于微前端页面使用服务商选择组件
              },
              {
                key: 'shared_wallet__record',
                href: '/agency/shared-wallet/record',
                purview: 22,
                isMicroApp: true,
                hideTabNav: true,
                // accountSelect: 'single', // 用于微前端页面使用服务商选择组件
              },
              {
                key: 'shared_wallet__monitor',
                href: '/agency/shared-wallet/exception',
                purview: 22,
                isMicroApp: true,
                hideTabNav: true,
                // accountSelect: 'single', // 用于微前端页面使用服务商选择组件
              },
            ],
          },
          {
            label: '资金预警',
            key: 'fund-alert',
            href: '/agency/finance/alert/balance',
            path: '/agency/finance/alert/:type',
            // purview: 22,
            platform: [AD_PLATFORM_TYPE_NAME.ADQ, AD_PLATFORM_TYPE_NAME.CPS],
            enableKey: 'enable_fund_alert_v1',
            isMicroApp: true,
            basePage: false,
            hideTabNav: true,
            data: [
              {
                key: 'fund-alert__balance',
                href: '/agency/finance/alert/balance',
                // purview: 22,
                isMicroApp: true,
                hideTabNav: true,
                // showSubNav: false,
              },
              {
                key: 'fund-alert__consume',
                href: '/agency/finance/alert/consume',
                // purview: 22,
                isMicroApp: true,
                hideTabNav: true,
              },
              {
                key: 'fund-alert__idle',
                href: '/agency/finance/alert/idle',
                // purview: 22,
                isMicroApp: true,
                hideTabNav: true,
              },
            ],
          },
          {
            label: '财务记录',
            href: '/agency/finance-record/tencent',
            path: '/agency/finance-record/:type',
            key: 'record',
            purview: 27,
            data: [
              {
                id: 'record',
                href: '/agency/finance-record/tencent',
                label: 'ADQ营销',
                platform: AD_PLATFORM_TYPE_NAME.ADQ,
                purview: 27,
                pagePurview: {
                  table: 28,
                  download: 29,
                },
                component: loadable(() => import(/* webpackChunkName: 'finance' */ /* webpackMode: 'lazy' */ '@/pages/agency/finance/record/tencent/Layout'), { fallback: <Loading /> }),
                basePage: true,
                behavior: {
                  downloadDay: '下载数据-日结报表',
                  fundtypeChange: '修改资金类型',
                  funddetails: '查看资金详情',
                  editColumn: '编辑自定义列',
                  changeColumn: '切换自定义列',
                  saveColumn: '保存自定义列',
                },
              },
              {
                id: 'record_wx',
                href: '/agency/finance-record/wechat',
                label: 'WX旧营销',
                tip: '原MP营销',
                platform: AD_PLATFORM_TYPE_NAME.MP,
                purview: 27,
                enableKey: 'is_wechat_finance_enable',
                pagePurview: {
                  table: 3030,
                  download: 3031,
                  ad_table: 3028,
                  ag_table: 3030,
                  ad_download: 3029,
                  ag_download: 3031,
                },
                component: loadable(() => import(/* webpackChunkName: 'finance' */ /* webpackMode: 'lazy' */ '@/pages/agency/finance/record/wx/Layout'), { fallback: <Loading /> }),
                basePage: true,
                behavior: {
                  downloadDay: '下载数据-日结报表',
                },
              },
              {
                id: 'record_recover_apply',
                href: '/agency/finance-record/recover-apply',
                label: '回划申请记录',
                platform: AD_PLATFORM_TYPE_NAME.MP,
                purview: 27,
                pagePurview: {
                  ad_table: 3028,
                  ag_table: 3030,
                  ad_download: 3029,
                  ag_download: 3031,
                },
                component: loadable(() => import(/* webpackChunkName: 'finance' */ /* webpackMode: 'lazy' */ '@/pages/agency/finance/record/wx/recover-apply'), { fallback: <Loading /> }),
                basePage: true,
                behavior: {
                  downloadDay: '下载数据-日结报表',
                },
                hide: true,
              },
              {
                id: 'record_cps',
                href: '/agency/finance-record/cps',
                label: 'CPS商品营销',
                platform: AD_PLATFORM_TYPE_NAME.CPS,
                purview: 27,
                enableKey: 'is_cps_agency_enable',
                pagePurview: {
                  purview: 27,
                  table: 28,
                  download: 29,
                },
                component: loadable(() => import(/* webpackChunkName: 'finance' */ /* webpackMode: 'lazy' */ '@/pages/agency/finance/record/cps/Layout'), { fallback: <Loading /> }),
                basePage: true,
                behavior: {
                  downloadDay: '下载数据-日结报表',
                  editColumn: '编辑自定义列',
                  changeColumn: '切换自定义列',
                  saveColumn: '保存自定义列',
                },
              },
            ],
          },
          {
            label: '信用金管理',
            href: '/agency/finance-credit/tencent',
            path: '/agency/finance-credit/:type',
            key: 'credit',
            purview: 30,
            data: [
              {
                id: 'credit',
                href: '/agency/finance-credit/tencent',
                label: '信用金管理',
                platform: [AD_PLATFORM_TYPE_NAME.ADQ, AD_PLATFORM_TYPE_NAME.MP],
                purview: 30,
                basePage: true,
                pagePurview: {
                  total: 3001,
                  bid: 3002,
                  bid_bill: 3003,
                  bid_bill_download: 3004,
                  remit: 3005,
                  bid_history_bill: 3007,
                  bid_history_bill_download: 3008,
                  bid_record: 3009,
                  bid_record_download: 3010,
                  contract: 3011,
                  contract_bill: 3012,
                  contract_bill_download: 3014,
                  contract_history_bill: 3015,
                  contract_history_bill_download: 3016,
                  contract_record: 3017,
                  contract_record_download: 3018,
                  cash_recharge: 3019,
                  get_remit_info: 3020,
                  creat_remit_info: 3021,
                  add_remit_info: 3022,
                  update_remit_info: 3023,
                },
                component: loadable(
                  () => import(
                    /* webpackChunkName: 'finance' */ /* webpackMode: 'lazy' */ '@/pages/agency/finance/credit/Layout'),
                  { fallback: <Loading /> },
                ),
              },
            ],
          },
          // v2
          {
            label: '日结报表',
            href: '/agency/finance/daily-report',
            key: 'daily_report',
            enableKey: 'enable_daily_report_v2',
            purview: 33,
            isMicroApp: true,
            hideTabNav: true,
            data: [
              {
                key: 'daily_report__list',
                href: '/agency/finance/daily-report',
                purview: 33,
                isMicroApp: true,
                hideTabNav: true,
              },
            ],
          },
          // v1
          {
            label: '日结报表',
            href: '/agency/finance-dailyreport',
            key: 'daily_report',
            disabled: (loginStatus: Record<string, any>) => loginStatus.feature_flag.enable_daily_report_v2, // 开启V2后隐藏V1入口
            // platform: [AD_PLATFORM_TYPE_NAME.ADQ, AD_PLATFORM_TYPE_NAME.CPS],
            platform: (loginStatus: Record<string, any>) => {
              if (!loginStatus.feature_flag.is_enable_mp_balance_report) {
                return [AD_PLATFORM_TYPE_NAME.ADQ, AD_PLATFORM_TYPE_NAME.CPS];
              }
            },
            purview: 33,
            component: loadable(() => import(/* webpackChunkName: 'finance' */ /* webpackMode: 'lazy' */ '@/pages/agency/finance/dailyreport/Layout'), { fallback: <Loading /> }),
            basePage: true,
            singleTabNav: true,
          },
          // V2
          {
            label: '发票管理',
            href: '/agency/finance-invoice/manage/list',
            key: 'invoice',
            purview: 30,
            enableKey: 'enable_invoice_v2',
            data: [
              {
                id: 'invoice',
                href: '/agency/finance-invoice/manage/list',
                platform: [AD_PLATFORM_TYPE_NAME.ADQ, AD_PLATFORM_TYPE_NAME.MP],
                purview: 30,
                isMicroApp: true,
                hideTabNav: true,
                pagePurview: {
                  invoice: 3041,
                  table: 3042,
                  apply_invoice: 3043,
                  cancel_invoice: 3044,
                  get_taxpayer_info: 3046,
                  update_taxpayer_info: 3047,
                  update_address_info: 3048,
                },
                behavior: {
                  invoice: '发票管理',
                },
              },
              {
                id: 'invoice_refund',
                href: '/agency/finance-invoice/manage/refund',
                platform: [AD_PLATFORM_TYPE_NAME.ADQ, AD_PLATFORM_TYPE_NAME.MP],
                purview: 30,
                isMicroApp: true,
                hideTabNav: true,
                pagePurview: {
                  invoice: 3041,
                  table: 3042,
                  apply_invoice: 3043,
                  cancel_invoice: 3044,
                  get_taxpayer_info: 3046,
                  update_taxpayer_info: 3047,
                  update_address_info: 3048,
                },
              },
              {
                hide: true,
                id: 'invoice_add',
                href: '/agency/finance-invoice/apply',
                label: '发票开具',
                platform: [AD_PLATFORM_TYPE_NAME.ADQ, AD_PLATFORM_TYPE_NAME.MP],
                isMicroApp: true,
                hideTabNav: true,
                purview: 3041,
                pagePurview: {
                  apply_invoice: 3043,
                  cancel_invoice: 3044,
                  get_taxpayer_info: 3046,
                  update_taxpayer_info: 3047,
                  update_address_info: 3048,
                },
              },
            ],
          },
          // V1 旧版本
          {
            label: '发票管理',
            href: '/agency/finance-invoice/tencent',
            path: '/agency/finance-invoice/:type',
            key: 'invoice',
            purview: 30,
            disabled: (loginStatus: Record<string, any>) => loginStatus.feature_flag.enable_invoice_v2, // 开启V2后隐藏V1入口
            data: [
              {
                id: 'invoice',
                href: '/agency/finance-invoice/tencent',
                platform: [AD_PLATFORM_TYPE_NAME.ADQ, AD_PLATFORM_TYPE_NAME.MP],
                purview: 30,
                isMicroApp: true,
                accountSelect: 'single', // 用于微前端页面使用服务商选择组件
                pagePurview: {
                  invoice: 3041,
                  table: 3042,
                  apply_invoice: 3043,
                  cancel_invoice: 3044,
                  get_taxpayer_info: 3046,
                  update_taxpayer_info: 3047,
                  update_address_info: 3048,
                },
                component: loadable(() => import(/* webpackChunkName: 'finance' */ /* webpackMode: 'lazy' */ '@/pages/agency/finance/invoice/tencent/Layout'), { fallback: <Loading /> }),
                behavior: {
                  invoice: '发票管理',
                },
              },
              {
                id: 'invoice_refund',
                href: '/agency/finance-invoice/refund',
                platform: [AD_PLATFORM_TYPE_NAME.ADQ, AD_PLATFORM_TYPE_NAME.MP],
                purview: 30,
                isMicroApp: true,
                accountSelect: 'single', // 用于微前端页面使用服务商选择组件
                pagePurview: {
                  invoice: 3041,
                  table: 3042,
                  apply_invoice: 3043,
                  cancel_invoice: 3044,
                  get_taxpayer_info: 3046,
                  update_taxpayer_info: 3047,
                  update_address_info: 3048,
                },
              },
              {
                hide: true,
                id: 'invoice_add',
                href: '/agency/finance-invoice/add',
                label: '发票开具',
                platform: [AD_PLATFORM_TYPE_NAME.ADQ, AD_PLATFORM_TYPE_NAME.MP],
                purview: 3041,
                pagePurview: {
                  apply_invoice: 3043,
                  cancel_invoice: 3044,
                  get_taxpayer_info: 3046,
                  update_taxpayer_info: 3047,
                  update_address_info: 3048,
                },
                component: loadable(() => import(/* webpackChunkName: 'finance' */ /* webpackMode: 'lazy' */ '@/pages/agency/finance/invoice/tencent/add/Layout'), { fallback: <Loading /> }),
                hideTabNav: true,
              },
            ],
          },
          {
            label: '返点返货查询',
            href: '/agency/finance-rebate',
            key: 'rebate',
            purview: 31,
            enableKey: 'is_new_rebate_enable',
            component: loadable(() => import(/* webpackChunkName: 'finance' */ /* webpackMode: 'lazy' */ '@/pages/agency/finance/rebate/list'), { fallback: <Loading /> }),
            basePage: true,
            singleTabNav: true,
          },
          {
            label: '返点返货申请',
            href: '/agency/finance-rebate-apply',
            key: 'rebate-apply',
            purview: 32, // 没有权限限制 跟财务模块同一权限
            component: loadable(() => import(/* webpackChunkName: 'finance' */ /* webpackMode: 'lazy' */ '@/pages/agency/finance/rebate/apply/product'), { fallback: <Loading /> }),
            basePage: true,
            singleTabNav: true,
          },
          // {
          //   label: '非框累量申请',
          //   href: '/agency/finance-rebate-frame-apply',
          //   key: 'rebate-apply',
          //   purview: 32, // 没有权限限制 跟财务模块同一权限
          //   component: loadable(() => import(/* webpackChunkName: 'finance' */ /* webpackMode: 'lazy' */ '@/pages/agency/finance/rebate/apply/frame'), { fallback: <Loading /> }),
          //   basePage: true,
          //   singleTabNav: true,
          // },
        ],
      },
    ],
  },
];
